import React, { useState, useEffect } from 'react';
import { getMatches, Match, createMatch, updateMatch, deleteMatch, MatchFormData, MatchStatus } from '../../api/matchesApi';
import { getTeams, Team } from '../../api/teamsApi';
import Layout from '../../components/Layout';
import { PencilIcon, TrashIcon, PlusIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import MatchFormModal from '../../components/MatchFormModal';
import ImportMatchesModal from '../../components/ImportMatchesModal';
import TableHeader from '../../components/TableHeader';
import SearchFilter from '../../components/SearchFilter';
import { sortData, filterData, SortState } from '../../utils/tableUtils';
import Table, { TableColumn } from '../../components/Table';

const Matches: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [displayedMatches, setDisplayedMatches] = useState<Match[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [currentMatch, setCurrentMatch] = useState<Match | undefined>(undefined);
  const [modalTitle, setModalTitle] = useState<string>('Add Match');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [matchToDelete, setMatchToDelete] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortState, setSortState] = useState<SortState<Match>>({
    key: 'match_date',
    direction: 'asc'
  });

  // Fetch matches and teams on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Apply sorting and filtering whenever the base data, search term, or sort state changes
  useEffect(() => {
    if (!Array.isArray(matches)) {
      console.error('Matches is not an array:', matches);
      setDisplayedMatches([]);
      return;
    }
    
    let filteredData = matches;
    
    // Apply search filtering
    if (searchTerm) {
      filteredData = filterData(
        filteredData, 
        searchTerm, 
        ['id', 'round', 'home_team_placeholder', 'away_team_placeholder', 'status']
      );
    }
    
    // Apply sorting
    filteredData = sortData(filteredData, sortState.key, sortState.direction);
    
    setDisplayedMatches(filteredData);
  }, [matches, searchTerm, sortState]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [fetchedMatches, fetchedTeams] = await Promise.all([
        getMatches(),
        getTeams()
      ]);
      
      // Ensure we're setting valid arrays
      setMatches(Array.isArray(fetchedMatches) ? fetchedMatches : []);
      setTeams(Array.isArray(fetchedTeams) ? fetchedTeams : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
      setMatches([]);
      setTeams([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get team name by ID
  const getTeamName = (teamId: number | null): string => {
    if (!teamId) return '';
    const team = teams.find((t) => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  // Function to format match date
  const formatMatchDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy-MM-dd HH:mm');
    } catch (err) {
      return dateString;
    }
  };

  // Function to get display format for scores
  const getScoreDisplay = (match: Match): string => {
    if (match.home_team_score !== null && match.away_team_score !== null) {
      return `${match.home_team_score} - ${match.away_team_score}`;
    }
    return '-';
  };

  // Function to get a display name for a result
  const getResultDisplay = (match: Match): string => {
    if (match.home_team?.id == match.winner) {
      return 'Home Win';
    } else if (match.away_team?.id == match.winner) {
      return 'Away Win';
    }

    return 'Draw';
  };

  // Function to get a display name for status
  const getStatusDisplay = (status: string): string => {
    switch (status) {
      case 'SCHEDULED': return 'Scheduled';
      case 'LIVE': return 'Live';
      case 'COMPLETED': return 'Completed';
      default: return status || 'Scheduled';
    }
  };

  // Handle sort column click
  const handleSort = (key: keyof Match) => {
    setSortState(prevState => ({
      key,
      direction: prevState.key === key && prevState.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Open modal for creating a new match
  const handleAddMatch = (): void => {
    setCurrentMatch(undefined);
    setModalTitle('Add Match');
    setIsModalOpen(true);
  };

  // Open modal for importing matches
  const handleImportMatches = (): void => {
    setIsImportModalOpen(true);
  };

  // Handle successful import
  const handleImportSuccess = (count: number): void => {
    setSuccessMessage(`Successfully imported ${count} matches`);
    fetchData(); // Refresh the matches list
  };

  // Open modal for editing an existing match
  const handleEditMatch = (match: Match): void => {
    setCurrentMatch(match);
    setModalTitle('Edit Match');
    setIsModalOpen(true);
  };

  // Open delete confirmation for a match
  const handleDeleteClick = (matchId: string): void => {
    setMatchToDelete(matchId);
    setShowDeleteConfirm(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    if (!matchToDelete) return;

    try {
      await deleteMatch(matchToDelete);
      setMatches(matches.filter(m => m.id !== matchToDelete));
      setShowDeleteConfirm(false);
      setMatchToDelete('');
      setSuccessMessage('Match deleted successfully');
    } catch (err) {
      console.error('Error deleting match:', err);
      setError('Failed to delete match. Please try again.');
    }
  };

  // Handle match form submission (create or update)
  const handleSubmitMatch = async (matchData: MatchFormData): Promise<void> => {
    try {
      let savedMatch: Match;
      
      if (currentMatch) {
        // Update existing match
        savedMatch = await updateMatch(currentMatch.id, matchData);
        setMatches(matches.map(m => m.id === savedMatch.id ? savedMatch : m));
        setSuccessMessage('Match updated successfully');
      } else {
        // Create new match
        savedMatch = await createMatch(matchData);
        setMatches([...matches, savedMatch]);
        setSuccessMessage('Match created successfully');
      }
    } catch (err) {
      console.error('Error saving match:', err);
      throw err;
    }
  };

  // Define table columns
  const columns: TableColumn<Match>[] = [
    {
      key: 'id',
      title: 'ID',
      render: (match) => <span className="text-gray-400">{match.id}</span>,
      sortable: true
    },
    {
      key: 'source_id',
      title: 'Source ID',
      render: (match) => <span className="text-gray-400">{match.source_id}</span>,
      sortable: true
    },
    {
      key: 'round',
      title: 'Round',
      render: (match) => <span className="text-white">{match.round}</span>,
      sortable: true
    },
    {
      key: 'home_team_placeholder',
      title: 'Home Team',
      render: (match) => <span className="text-white">
        {match.home_team_id ? getTeamName(match.home_team_id) : match.home_team_placeholder}
      </span>,
      sortable: true
    },
    {
      key: 'away_team_placeholder',
      title: 'Away Team',
      render: (match) => <span className="text-white">
        {match.away_team_id ? getTeamName(match.away_team_id) : match.away_team_placeholder}
      </span>,
      sortable: true
    },
    {
      key: 'home_team_score',
      title: 'Score',
      render: (match) => <span className="text-stake-blue font-medium">{getScoreDisplay(match)}</span>,
      sortable: true
    },
    {
      key: 'status',
      title: 'Status',
      render: (match) => {
        let statusClass = "px-2 inline-flex text-xs leading-5 font-semibold rounded-full";
        if (match.status === MatchStatus.SCHEDULED) {
          statusClass += " bg-blue-100 text-blue-800";
        } else if (match.status === MatchStatus.LIVE) {
          statusClass += " bg-stake-green bg-opacity-30 text-stake-green";
        } else if (match.status === MatchStatus.COMPLETED) {
          statusClass += " bg-gray-100 text-gray-800";
        }
        return <span className={statusClass}>{getStatusDisplay(match.status)}</span>;
      },
      sortable: true
    },
    {
      key: 'result',
      title: 'Result',
      render: (match) => <span className="text-white">{getResultDisplay(match)}</span>,
      sortable: true
    },
    {
      key: 'match_date',
      title: 'Date',
      render: (match) => <span className="text-gray-300">{formatMatchDate(match.match_date)}</span>,
      sortable: true
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (match) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditMatch(match)}
            className="text-stake-blue hover:text-blue-300 p-1 rounded hover:bg-dark-200 transition-colors"
            title="Edit Match"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDeleteClick(match.id)}
            className="text-stake-red hover:text-red-400 p-1 rounded hover:bg-dark-200 transition-colors"
            title="Delete Match"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="-mx-3 sm:mx-0 sm:px-4 py-4">
        <div className="flex justify-between items-center mb-6 px-3 sm:px-0">
          <div className="flex items-center space-x-3">
            <SearchFilter
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search matches..."
              className="w-64 bg-dark-200 border-stake-border text-gray-300"
            />
            <button
              className="inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-stake-blue hover:bg-primary-600 transition duration-150"
              onClick={handleImportMatches}
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Import
            </button>
            <button
              className="inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-stake-blue hover:bg-primary-600 transition duration-150"
              onClick={handleAddMatch}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Match
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-stake-red bg-opacity-20 border border-stake-red text-white px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-stake-green bg-opacity-20 border border-stake-green text-white px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        <Table<Match>
          fullWidth
          compact
          data={displayedMatches}
          columns={columns}
          keyExtractor={(match) => match.id}
          emptyMessage="No matches found"
          isLoading={isLoading && !isModalOpen}
          sortState={sortState}
          onSort={handleSort}
          searchTerm={searchTerm}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-100 rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
              <h3 className="text-xl font-medium text-white mb-4">Confirm Delete</h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete this match? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-300 border border-stake-border rounded-md hover:bg-dark-400 transition-colors"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-stake-red border border-transparent rounded-md hover:bg-red-700 transition-colors"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Match Form Modal */}
        <MatchFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmitMatch}
          match={currentMatch}
          title={modalTitle}
          teams={teams}
        />

        {/* Import Matches Modal */}
        <ImportMatchesModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={handleImportSuccess}
          teams={teams}
        />
      </div>
    </Layout>
  );
};

export default Matches; 